import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  effect,
  input,
  viewChild,
} from '@angular/core';
import { IconModule } from '../../../atoms/icon/icon.module';
import { TextModule } from '../../../atoms/text/text.module';

@Component({
  selector: 'app-radio-button-group',
  templateUrl: './radio-button-group.component.html',
  styleUrls: ['./radio-button-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'group',
    '[attr.size]': 'size()',
  },
  imports: [CommonModule, IconModule, TextModule],
})
export class RadioButtonGroupComponent implements AfterViewInit, OnDestroy {
  // @deprecated
  readonly selectedIndex = input<number>(undefined);

  readonly size = input<string>('default');

  private readonly container = viewChild<ElementRef<HTMLDivElement>>('container');
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  constructor() {
    // Re-run layout calculations whenever the selected index signal changes.
    effect(() => {
      if (!this.container()) return;
      this.scheduleIndicatorUpdate(this.selectedIndex());
    });
  }

  ngAfterViewInit(): void {
    // Wait until projected content is stamped to avoid measuring stale DOM.
    queueMicrotask(() => {
      this.observeContainer();
      this.updateIndicator(this.selectedIndex());
      this.enableTransition();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private observeContainer(): void {
    const containerElement = this.container()?.nativeElement;
    if (!containerElement) {
      return;
    }

    // Keep indicator aligned when container resizes (labels change, responsive layout, etc.).
    this.resizeObserver = new ResizeObserver(() => {
      this.updateIndicator(this.selectedIndex());
    });
    this.resizeObserver.observe(containerElement);

    // React to class toggles (selected state) or DOM changes inside the slot.
    this.mutationObserver = new MutationObserver(() => {
      this.updateIndicator(this.selectedIndex());
    });
    this.mutationObserver.observe(containerElement, {
      subtree: true,
      childList: true,
      attributeFilter: ['class'],
    });
  }

  private scheduleIndicatorUpdate(index?: number | null): void {
    queueMicrotask(() => this.updateIndicator(index));
  }

  private enableTransition(): void {
    const containerElement = this.container()?.nativeElement;
    if (!containerElement) return;

    // Defer enabling transitions so the first paint doesn't animate from 0px.
    requestAnimationFrame(() => {
      containerElement.style.setProperty('--rb-transition', 'transform 250ms ease, width 250ms ease');
    });
  }

  private updateIndicator(index?: number | null): void {
    const containerElement = this.container()?.nativeElement;
    if (!containerElement) return;

    const items = Array.from(containerElement.querySelectorAll<HTMLElement>('app-radio-button-item'));
    if (!items.length) {
      containerElement.style.removeProperty('--rb-indicator-x');
      containerElement.style.removeProperty('--rb-indicator-width');
      return;
    }

    // Prefer explicit index, otherwise fall back to the child reporting `.selected`.
    let target = typeof index === 'number' && index >= 0 ? items[index] : undefined;
    if (!target) {
      target = items.find((item) => item.classList.contains('selected')) ?? items[0];
    }

    const containerRect = containerElement.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    containerElement.style.setProperty('--rb-indicator-x', `${targetRect.left - containerRect.left}px`);
    containerElement.style.setProperty('--rb-indicator-width', `${targetRect.width}px`);
  }
}
